import React, { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { getBEMClassName, useReactHookFormValidationHelpers } from '@netfront/common-library';
import { FORM_ELEMENT_CSS_IDENTIFIERS, FORM_FIELDS } from '@netfront/gelada-identity-library';
import { Button, ControlledForm, FormFieldProps, Input, Textarea, useControlledForm } from '@netfront/ui-library';
import { Controller, Resolver } from 'react-hook-form';
import * as yup from 'yup';

import { CONTACT_FORM_BLOCK_CSS_IDENTIFIERS, CONTACT_FORM_SPAM_RULES } from './ContactForm.constants';
import { IContactForm } from './ContactForm.interfaces';

const ContactForm = ({ buttonText = 'Send', isSubmitting, onSend }: IContactForm) => {
  const { form: formElementCssId } = FORM_ELEMENT_CSS_IDENTIFIERS;

  const [defaultValues, setDefaultValues] = useState<FormFieldProps>({ 
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    phoneNumber: '',
    organisationName: ''
  });

  const {
    email: { id: emailId, label: emailLabel, placeholder: emailPlaceholder },
    firstName: { id: firstNameId, label: firstNameLabel, placeholder: firstNamePlaceholder },
    lastName: { id: lastNameId, label: lastNameLabel, placeholder: lastNamePlaceholder },
    message: { id: messageId, label: messageLabel },
    organisation: { id: organisationId, label: organisationLabel },
    phoneNumber: { id: phoneNumberId, label: phoneNumberLabel },
  } = FORM_FIELDS;

  const { control, handleSubmit, reset } = useControlledForm({
    defaultValues,
    resolver: yupResolver(
      yup.object().shape({
        firstName: yup.string().label(firstNameLabel).required(),
        lastName: yup.string().label(lastNameLabel).required(),
        message: yup.string().label(messageLabel).required(),
        organisationName: yup.string().label(organisationLabel).required(),
        email: yup.string().label(emailLabel).email('Please enter a valid email').required(),
      }),
    ) as unknown as Resolver<FormFieldProps>,
    spamRules: CONTACT_FORM_SPAM_RULES,
  });

  const { form: formBlockCssId } = CONTACT_FORM_BLOCK_CSS_IDENTIFIERS;

  const { getFormFieldErrorMessage } = useReactHookFormValidationHelpers();

  const onSubmit = (item: FormFieldProps, headers?: {[key: string]: string} ) => {

    const {
      firstName,
      lastName,
      email,
      message,
      phoneNumber,
      organisationName,
    } = item;
    
    onSend({
      firstName,
      lastName,
      email,
      message,
      phoneNumber,
      organisationName,
    }, headers);

    const resetValues = {
      firstName: '',
      lastName: '',
      email: '',
      message: '',
      phoneNumber: '',
      organisationName: ''
    };

    reset(resetValues);
    setDefaultValues(resetValues);
  };

  useEffect(() => {
    setDefaultValues({
      firstName: '',
      lastName: '',
      email: '',
      message: '',
      phoneNumber: '',
      organisationName: ''
    });
  }, []);

  return (
    <div className={getBEMClassName(formBlockCssId, formElementCssId)}>
      <ControlledForm
        action="contact_form"
        callBack={(item: FormFieldProps, headers?: {[key: string]: string}) => {
          void onSubmit(item, headers);
        }}
        handleSubmit={handleSubmit}
        projectId="quickcodes"
        // eslint-disable-next-line no-console
        onSubmitError={(errs) => console.log({errs})}
      >
        <div className="c-form__header" />
        <div className="c-form__body">
          <div className="flex -mx-2">
            <div className="c-form__field w-1/2 px-2">
              <Controller
                control={control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <Input
                    additionalClassNames={getBEMClassName(formBlockCssId, firstNameId)}
                    errorMessage={getFormFieldErrorMessage(fieldState)}
                    id={`${firstNameId}-contact`}
                    isDisabled={isSubmitting}
                    labelText={firstNameLabel}
                    placeholder={firstNamePlaceholder}
                    type="text"
                    isRequired
                    {...field}
                  />
                )}
              />
            </div>
            <div className="c-form__field w-1/2 px-2">
              <Controller
                control={control}
                name="lastName"
                render={({ field, fieldState }) => (
                  <Input
                    additionalClassNames={getBEMClassName(formBlockCssId, lastNameId)}
                    errorMessage={getFormFieldErrorMessage(fieldState)}
                    id={`${lastNameId}-contact`}
                    isDisabled={isSubmitting}
                    labelText={lastNameLabel}
                    placeholder={lastNamePlaceholder}
                    type="text"
                    isRequired
                    {...field}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex -mx-2">
            <div className="c-form__field w-1/2 px-2">
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                  <Input
                    additionalClassNames={getBEMClassName(formBlockCssId, emailId)}
                    errorMessage={getFormFieldErrorMessage(fieldState)}
                    id={`${emailId}-contact`}
                    isDisabled={isSubmitting}
                    labelText={emailLabel}
                    placeholder={emailPlaceholder}
                    type="email"
                    isRequired
                    {...field}
                  />
                )}
              />
            </div>
            <div className="c-form__field w-1/2 px-2">
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field, fieldState }) => (
                  <Input
                    additionalClassNames={getBEMClassName(formBlockCssId, phoneNumberId)}
                    errorMessage={getFormFieldErrorMessage(fieldState)}
                    id={`${phoneNumberId}-contact`}
                    isDisabled={isSubmitting}
                    labelText={phoneNumberLabel}
                    type="text"
                    {...field}
                  />
                )}
              />
            </div>
          </div>
          <div className="c-form__field">
            <Controller
              control={control}
              name="organisationName"
              render={({ field, fieldState }) => (
                <Input
                  additionalClassNames={getBEMClassName(formBlockCssId, organisationId)}
                  errorMessage={getFormFieldErrorMessage(fieldState)}
                  id={`${organisationId}-contact`}
                  isDisabled={isSubmitting}
                  labelText={organisationLabel}
                  type="text"
                  isRequired
                  {...field}
                />
              )}
            />
          </div>
          <div className="c-form__field">
            <Controller
              control={control}
              name="message"
              render={({ field, fieldState }) => (
                <Textarea
                  additionalClassNames={getBEMClassName(formBlockCssId, messageId)}
                  errorMessage={getFormFieldErrorMessage(fieldState)}
                  id={`${messageId}-contact`}
                  isDisabled={isSubmitting}
                  labelText={messageLabel}
                  isRequired
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className="c-form__footer">
          <Button isDisabled={isSubmitting} text={buttonText} type="submit" />
        </div>
      </ControlledForm>
    </div>
  );
};

export { ContactForm };
